import { useEffect, useState, useRef } from 'react';
import { motion } from 'motion/react';
import { useWorkoutStore } from '@/lib/stores/workoutStore';
import { useAuthStore } from '@/lib/stores/authStore';
import { GenerateWorkoutCard } from './GenerateWorkoutCard';
import { QuickWorkoutCard } from './QuickWorkoutCard';
import { PresetWorkoutCard } from './PresetWorkoutCard';
import { UserMenu } from './UserMenu';
import { AuthModal } from '@/components/auth/AuthModal';
import { workoutApi } from '@/services/workoutApi';
import { Workout } from '@/types/workout';
import { WorkoutGenerationCarousel } from '@/components/generate/WorkoutGenerationCarousel';

interface HomeScreenProps {
  onGenerateClick: () => void;
  onWorkoutSelect: (workoutIdOrWorkout: string | Workout) => void;
}

export function HomeScreen({ onGenerateClick, onWorkoutSelect }: HomeScreenProps) {
  const { presetWorkouts, generatedWorkouts, loadPresets, loadGeneratedWorkouts, loading, error } = useWorkoutStore();
  const { isAuthenticated, isLoading: authLoading, user } = useAuthStore();
  const [isLoading, setIsLoading] = useState(true);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [isGeneratingQuickWorkout, setIsGeneratingQuickWorkout] = useState(false);
  const hasInitializedRef = useRef(false);
  const prevAuthenticatedRef = useRef(isAuthenticated);

  useEffect(() => {
    // Track if this is the initial mount or an auth state change
    const isInitialMount = !hasInitializedRef.current;
    const authChanged = prevAuthenticatedRef.current !== isAuthenticated;
    prevAuthenticatedRef.current = isAuthenticated;

    // Prevent duplicate calls from React.StrictMode on initial mount
    if (isInitialMount) {
      hasInitializedRef.current = true;
      loadPresets();
    }

    // Load workouts when:
    // 1. Initial mount and user is authenticated
    // 2. Auth state changes to authenticated
    if (isAuthenticated && (isInitialMount || authChanged)) {
      loadGeneratedWorkouts().finally(() => setIsLoading(false));
    } else if (!isAuthenticated && isInitialMount) {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]); // Only depend on isAuthenticated, store functions are stable

  const handleShowAuth = () => {
    setShowAuthModal(true);
  };

  const handleAuthSuccess = () => {
    // After successful auth, load workouts and stay on home screen
    loadGeneratedWorkouts();
  };

  const handleQuickWorkout = async () => {
    setIsGeneratingQuickWorkout(true);
    try {
      // Generate a personalized 3-minute workout via gym_agent
      const workout = await workoutApi.generateWorkout(
        "create and store a short 3 minute core workout."
      );
      
      // Refresh workouts list to include the new one (async, doesn't block navigation)
      loadGeneratedWorkouts();
      
      // Navigate to workout overview with the workout object directly
      onWorkoutSelect(workout);
    } catch (error) {
      console.error('Failed to generate quick workout:', error);
      // Error is already handled by the store, but we can show user feedback if needed
    } finally {
      setIsGeneratingQuickWorkout(false);
    }
  };

  return (
    <>
      <WorkoutGenerationCarousel isVisible={isGeneratingQuickWorkout} />
      <div className="min-h-screen bg-white">
        <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onSuccess={handleAuthSuccess}
      />
      <div className="max-w-2xl mx-auto px-6 py-16">
        {/* User Menu - Always visible, adapts to auth state */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <div className="flex items-center justify-between mb-4">
            <UserMenu onShowAuth={handleShowAuth} />
          </div>
        </motion.div>

        {/* Workout generation cards */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="space-y-4 mb-8"
        >
          {/* Generate workout card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <GenerateWorkoutCard onGenerate={onGenerateClick} onShowAuth={handleShowAuth} />
          </motion.div>

          {/* Quick workout card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
          >
            <QuickWorkoutCard 
              onQuickWorkout={handleQuickWorkout} 
              isLoading={isGeneratingQuickWorkout}
              onShowAuth={handleShowAuth}
            />
          </motion.div>
        </motion.div>

        {/* Divider */}
        <div className="py-4">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-neutral-200"></div>
            </div>
            <div className="relative flex justify-center">
              <span className="px-4 bg-white text-neutral-400 text-sm">
                Workouts
              </span>
            </div>
          </div>
        </div>

        {/* Workout list */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="space-y-4"
        >

          {/* Error message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800 text-sm">
              {error}
            </div>
          )}

          {/* Loading state */}
          {isLoading && (
            <div className="text-center text-neutral-500 py-8">
              Loading workouts...
            </div>
          )}

          {/* Empty state */}
          {isAuthenticated && !isLoading && generatedWorkouts.length === 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-center py-12"
            >
              <p className="text-neutral-600 mb-2">No saved workouts yet</p>
              <p className="text-neutral-500 text-sm">Generate or start a quick workout to get started</p>
            </motion.div>
          )}

          {/* User workouts (only when authenticated) */}
          {isAuthenticated && !isLoading && generatedWorkouts.length > 0 && (
            <>
              {generatedWorkouts.map((workout, index) => (
                <motion.div
                  key={workout.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.15 + index * 0.05 }}
                >
                  <PresetWorkoutCard
                    workout={workout}
                    onStart={() => onWorkoutSelect(workout.id)}
                  />
                </motion.div>
              ))}
            </>
          )}

          {/* Preset workouts (only when not authenticated) */}
          {!isAuthenticated && presetWorkouts.map((workout, index) => (
            <motion.div
              key={workout.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 + index * 0.05 }}
            >
              <PresetWorkoutCard
                workout={workout}
                onStart={() => onWorkoutSelect(workout.id)}
              />
            </motion.div>
          ))}
        </motion.div>
      </div>
    </div>
    </>
  );
}

